/**
 * Sistema de Dicas Contextuais
 * Motor de regras que define quais dicas mostrar baseado na página e dados disponíveis
 */

/**
 * Define todas as regras de dicas para cada página
 * @param {string} pageName - Nome da página (dashboard, professionals, services, places, schedules)
 * @param {object} pageData - Dados do escopo da página (stats, items, etc.)
 * @returns {object|null} - Dica a ser exibida { type, icon, message, action: { label, link } }
 */
export const getHintForPage = (pageName, pageData = {}) => {
  // ✅ Hints agora são avaliados pelo contexto global (stats) sempre que possível.
  // pageName continua existindo apenas para ajustes contextuais (ex: schedules com shifts).
  const globalHint = getGlobalHint(pageName, pageData);
  if (globalHint) return globalHint;

  // Fallback: mantém regras antigas quando não há stats suficientes
  const rules = {
    dashboard: () => getDashboardHint(pageData),
    professionals: () => getProfessionalsHint(pageData),
    services: () => getServicesHint(pageData),
    places: () => getPlacesHint(pageData),
    schedules: () => getSchedulesHint(pageData),
  };

  const ruleFunction = rules[pageName];
  return ruleFunction ? ruleFunction() : getDefaultHint();
};

const hashStringToInt = (value) => {
  if (!value) return 0;
  let hash = 0;
  for (let i = 0; i < value.length; i++) {
    hash = ((hash << 5) - hash) + value.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash);
};

const pickByDay = (options, salt = '') => {
  if (!Array.isArray(options) || options.length === 0) return null;
  // Determinístico por "salt" (que pode incluir rota + seed)
  const key = `${salt}`;
  const idx = hashStringToInt(key) % options.length;
  return options[idx];
};

/**
 * Hint global (independente da página): guia onboarding e depois sugere "você sabia".
 *
 * Espera receber stats em pageData.stats (preferencial), ou pageData.globalStats.
 */
const getGlobalHint = (pageName, pageData) => {
  const stats = pageData?.stats || pageData?.globalStats;
  if (!stats) return null;

  const routePath = pageData?.routePath || '';
  const navSeed = pageData?.navSeed ?? 0;
  const routeSalt = `${routePath}-${navSeed}`;

  const professionals = stats.professionals ?? 0;
  const services = stats.services ?? 0;
  const places = stats.places ?? 0;
  const schedules = stats.schedules ?? 0;

  // 0) Zero de tudo -> empurrar o wizard de Local (atalho que acelera tudo)
  if (professionals === 0 && services === 0 && places === 0 && schedules === 0) {
    return {
      type: 'action-required',
      icon: '🚀',
      message: 'Vamos começar do jeito mais rápido: crie seu primeiro local usando o wizard. Ele já te guia para configurar profissionais e serviços e deixar tudo pronto para receber agendamentos.',
      action: {
        label: 'Começar pelo Wizard',
        link: '/places?wizard=1'
      }
    };
  }

  // 1) Sem profissionais
  if (professionals === 0) {
    const variants = [
      {
        type: 'action-required',
        icon: '👥',
        message: 'Você ainda não tem profissionais cadastrados. Profissionais são as pessoas que realizam os atendimentos (médicos, terapeutas, barbeiros, etc).',
      },
      {
        type: 'action-required',
        icon: '👤',
        message: 'Cadastre pelo menos 1 profissional para conseguir montar agendas e liberar horários para seus clientes.',
      },
    ];
    const picked = pickByDay(variants, `no-professionals-${routeSalt}`) || variants[0];
    return {
      ...picked,
      action: {
        label: 'Criar Profissional',
        link: '/professionals?create=1'
      }
    };
  }

  // 2) Tem profissionais, mas sem serviços
  if (services === 0) {
    const variants = [
      {
        type: 'next-step',
        icon: '⚙️',
        message: 'Ótimo: profissionais prontos. Agora cadastre os serviços (procedimentos/consultas) com duração e categoria.',
      },
      {
        type: 'next-step',
        icon: '🧾',
        message: 'Próximo passo: criar serviços. Eles definem o que pode ser agendado e ajudam a organizar a operação.',
      },
    ];
    const picked = pickByDay(variants, `no-services-${routeSalt}`) || variants[0];
    return {
      ...picked,
      action: {
        label: 'Criar Serviço',
        link: '/services?create=1'
      }
    };
  }

  // 3) Tem profissionais + serviços, mas sem locais
  if (places === 0) {
    const variants = [
      {
        type: 'next-step',
        icon: '📍',
        message: 'Agora cadastre seu(s) local(is) de atendimento. Cada local pode ter profissionais e serviços diferentes.',
      },
      {
        type: 'next-step',
        icon: '🏢',
        message: 'Próximo passo: criar um local. É nele que você vai vincular profissionais e serviços e deixar tudo pronto pra agenda online.',
      },
    ];
    const picked = pickByDay(variants, `no-places-${routeSalt}`) || variants[0];
    return {
      ...picked,
      action: {
        label: 'Criar Local (Wizard)',
        link: '/places?wizard=1'
      }
    };
  }

  // 4) Tem tudo base, mas sem agendas
  if (schedules === 0) {
    const variants = [
      {
        type: 'next-step',
        icon: '📅',
        message: 'Tudo pronto! Agora crie uma agenda para definir a disponibilidade de cada profissional em cada local e começar a receber agendamentos online.',
      },
      {
        type: 'next-step',
        icon: '🗓️',
        message: 'Último passo de configuração: criar uma agenda (profissional + local). Depois é só configurar os turnos.',
      },
    ];
    const picked = pickByDay(variants, `no-schedules-${routeSalt}`) || variants[0];
    return {
      ...picked,
      action: {
        label: 'Criar Agenda',
        link: '/schedules?wizard=1'
      }
    };
  }

  // 5) Ajuste contextual: em /schedules, se ainda não configurou serviços nos turnos
  if (pageName === 'schedules') {
    const items = pageData?.items || [];
    const shifts = pageData?.shifts || {};

    // Aguarda o carregamento dos turnos antes de avaliar.
    // shifts é populado async; se ainda não tem entradas para nenhuma agenda,
    // não exibe o hint para evitar falso positivo durante o loading.
    const shiftsLoaded = items.length === 0 || Object.keys(shifts).length > 0;

    if (shiftsLoaded) {
      // O backend retorna shift.services (array de objetos com serviceId/serviceName).
      // Suporte também a shift.serviceIds por compatibilidade futura.
      const hasShiftsWithServices = items.some(schedule => {
        const scheduleShifts = shifts[schedule.id] || [];
        return scheduleShifts.some(shift => {
          const servicesArr = shift.services || shift.Services || [];
          const serviceIdsArr = shift.serviceIds || shift.ServiceIds || [];
          return servicesArr.length > 0 || serviceIdsArr.length > 0;
        });
      });

      if (!hasShiftsWithServices) {
        return {
          type: 'next-step',
          icon: '⏰',
          message: 'Agendas criadas! Agora configure os turnos (dias/horários) e selecione quais serviços podem ser agendados em cada turno. Isso libera os horários para seus clientes.',
          action: {
            label: 'Ir para Agendas',
            link: '/schedules'
          }
        };
      }
    }
  }

  // 6) Tudo configurado -> dicas rotativas
  const tips = [
    {
      type: 'tip',
      icon: '📣',
      message: 'Você já pode criar uma página estilo “link na bio” para divulgar nas redes sociais e centralizar seus links e botões.',
      action: { label: 'Criar Página', link: '/pages' }
    },
    {
      type: 'tip',
      icon: '🏖️',
      message: 'Você sabia? Dá para cadastrar férias e folgas para bloquear horários automaticamente e evitar agendamentos no período.',
      action: { label: 'Ver Ausências', link: '/absences' }
    },
    {
      type: 'tip',
      icon: '📞',
      message: 'Dica: mantenha seus pacientes/contatos organizados para agilizar o atendimento e reduzir erros na hora de agendar.',
      action: { label: 'Ver Pacientes', link: '/persons' }
    },

    // ✅ Novas dicas (venda do sistema / benefícios)
    {
      type: 'tip',
      icon: '🔗',
      message: 'Venda sem esforço: compartilhe o link público da agenda e deixe o cliente escolher horário sozinho, 24h por dia. Menos mensagens, mais agenda cheia.',
      action: { label: 'Compartilhar Agenda', link: '/schedules' }
    },
    {
      type: 'tip',
      icon: '⏰',
      message: 'Você sabia? Em cada turno você escolhe quais serviços ficam disponíveis. Isso evita agendarem “o serviço errado” no horário errado — e reduz retrabalho.',
      action: { label: 'Configurar Turnos', link: '/schedules' }
    },
    {
      type: 'tip',
      icon: '🧩',
      message: 'Operação flexível: crie agendas por local e profissional (ex.: Clínica X vs. Unidade Y) e mantenha tudo centralizado no mesmo painel.',
      action: { label: 'Gerenciar Agendas', link: '/schedules' }
    },
    {
      type: 'tip',
      icon: '🚀',
      message: 'Atalho de produtividade: o wizard ajuda a criar agendas mais rápido escolhendo o local e gerando as agendas necessárias em poucos cliques.',
      action: { label: 'Abrir Wizard', link: '/schedules?wizard=1' }
    },
    {
      type: 'tip',
      icon: '💳',
      message: 'Profissionalismo vende: crie métodos de pagamento com nomes claros (ex.: “Pix”, “Cartão”, “Convênio X”) e padronize como seus clientes veem isso.',
      action: { label: 'Configurar Pagamentos', link: '/payment-methods' }
    },
    {
      type: 'tip',
      icon: '⚙️',
      message: 'Você sabia? Dá para definir formas de pagamento por serviço e por local. Ideal para regras do tipo “Convênio só na unidade A” ou “Serviço premium só no cartão”.',
      action: { label: 'Ajustar por Local', link: '/places' }
    },
    {
      type: 'tip',
      icon: '📍',
      message: 'Multiunidade de verdade: cadastre vários locais e personalize equipe, serviços e pagamentos de cada um — perfeito para expansão sem perder controle.',
      action: { label: 'Gerenciar Locais', link: '/places' }
    },
    {
      type: 'tip',
      icon: '👥',
      message: 'Time crescendo? Adicione profissionais e distribua atendimentos por local. Isso melhora disponibilidade e aumenta a capacidade de agendamentos.',
      action: { label: 'Gerenciar Profissionais', link: '/professionals' }
    },
    {
      type: 'tip',
      icon: '📷',
      message: 'Detalhe que converte: suba a foto do profissional. A página fica mais confiável e ajuda o cliente a escolher com segurança.',
      action: { label: 'Atualizar Fotos', link: '/professionals' }
    },
    {
      type: 'tip',
      icon: '🔎',
      message: 'Atendimento ágil: use “Próximos horários disponíveis” para achar rapidamente o primeiro encaixe e fechar a venda no momento da conversa.',
      action: { label: 'Ver Profissionais', link: '/professionals' }
    },
    {
      type: 'tip',
      icon: '🧾',
      message: 'Serviços bem definidos vendem mais: configure duração e preço padrão para o cliente entender valor e para sua agenda ficar mais previsível.',
      action: { label: 'Revisar Serviços', link: '/services' }
    },
    {
      type: 'tip',
      icon: '🏷️',
      message: 'Organização = escala: use categorias de serviços para deixar tudo mais fácil de achar e mais claro no dia a dia do time.',
      action: { label: 'Organizar Serviços', link: '/services' }
    },
    {
      type: 'tip',
      icon: '✅',
      message: 'Você sabia? Dá para criar agendamentos direto da tela de serviços e profissional e acelerar o atendimento quando o cliente já escolheu o procedimento.',
      action: { label: 'Agendar por Serviço', link: '/services' }
    },
    {
      type: 'tip',
      icon: '🗓️',
      message: 'Controle total: confirme, cancele, remarque e marque como atendido. Você mantém a agenda limpa e a operação rodando sem confusão.',
      action: { label: 'Gerenciar Agendamentos', link: '/appointments' }
    },
    {
      type: 'tip',
      icon: '🎯',
      message: 'Dica de operação: filtre agendamentos por profissional, local e status. Em 10 segundos você enxerga o que exige ação agora.',
      action: { label: 'Usar Filtros', link: '/appointments' }
    },
    {
      type: 'tip',
      icon: '🔵',
      message: 'Qualidade de dados: marque “Atendido” no fim do atendimento. Isso separa concluídos de pendentes e ajuda a enxergar no-show com clareza.',
      action: { label: 'Atualizar Status', link: '/appointments' }
    },
    {
      type: 'tip',
      icon: '♻️',
      message: 'Remarcação sem drama: ao remarcar, você reaproveita os dados do cliente e ganha velocidade para não perder o agendamento.',
      action: { label: 'Remarcar', link: '/appointments' }
    },
    {
      type: 'tip',
      icon: '🧠',
      message: 'Base de clientes é ativo: cadastre pessoas com nome e telefone e depois complete com e-mail/CPF/notas. Você cria histórico e personaliza atendimento.',
      action: { label: 'Gerenciar Pessoas', link: '/persons' }
    },
    {
      type: 'tip',
      icon: '🔍',
      message: 'Você sabia? Dá para buscar pessoas por nome, telefone, e-mail ou CPF. Ideal para atendimento rápido na recepção.',
      action: { label: 'Buscar Pessoas', link: '/persons' }
    },
    {
      type: 'tip',
      icon: '📝',
      message: 'Dica “premium”: use notas do cliente (preferências, alergias, observações). Parece pequeno, mas aumenta retenção e melhora a experiência.',
      action: { label: 'Ver Pessoas', link: '/persons' }
    },
    {
      type: 'tip',
      icon: '📲',
      message: 'Marketing pronto: crie uma página Link-in-Bio com botões, redes sociais e um CTA direto para agendar. Perfeito para Instagram e anúncios.',
      action: { label: 'Páginas e Links', link: '/pages' }
    },
    {
      type: 'tip',
      icon: '🎨',
      message: 'Você sabia? O editor de páginas tem preview para você ajustar o visual antes de publicar. Resultado: página bonita, clara e com mais conversão.',
      action: { label: 'Editar Páginas', link: '/pages' }
    },
    {
      type: 'tip',
      icon: '🧲',
      message: 'Estratégia avançada: crie páginas diferentes por unidade ou por profissional e compartilhe o link certo em cada campanha. (EM BREVE)',
      action: { label: 'Criar Variações', link: '/pages' }
    },
    {
      type: 'tip',
      icon: '🧱',
      message: 'Agenda protegida: cadastre ausências em massa (férias/feriados/compromissos) para bloquear horários automaticamente e evitar overbooking.',
      action: { label: 'Bloquear Períodos', link: '/absences' }
    },
    {
      type: 'tip',
      icon: '🛟',
      message: 'Precisa de ajuda ou quer sugerir melhoria? Abra um ticket direto no painel. Suporte rápido, contexto completo, solução mais ágil.',
      action: { label: 'Abrir Suporte', link: '/support-tickets' }
    },
    {
      type: 'tip',
      icon: '🔒',
      message: 'Confiança também vende: seus termos e política de privacidade ajudam a passar segurança para clientes e para a operação (LGPD).',
      action: { label: 'Ver Termos', link: '/termos-de-uso' }
    },
  ];

  return pickByDay(tips, `configured-${routeSalt}`) || tips[0];
};

/**
 * Regras para a página Dashboard
 */
const getDashboardHint = (data) => {
  const { stats = {} } = data;
  const { professionals = 0, services = 0, places = 0, schedules = 0 } = stats;

  // Prioridade 1: Sem profissionais
  if (professionals === 0) {
    return {
      type: 'action-required',
      icon: '👥',
      message: 'Você ainda não tem profissionais cadastrados. Profissionais são as pessoas que vão realizar os atendimentos (médicos, dentistas, terapeutas, barbeiros, etc). Comece criando seu primeiro profissional para dar o pontapé inicial!',
      action: {
        label: 'Ir para Profissionais',
        link: '/professionals'
      }
    };
  }

  // Prioridade 2: Tem profissionais, mas sem serviços
  if (services === 0) {
    return {
      type: 'next-step',
      icon: '⚙️',
      message: 'Ótimo! Agora você precisa cadastrar os serviços que seus profissionais oferecem. Serviços são os procedimentos, consultas ou atendimentos que você disponibiliza (ex: corte de cabelo, limpeza de pele, consulta médica). Defina a duração e o preço de cada um!',
      action: {
        label: 'Ir para Serviços',
        link: '/services'
      }
    };
  }

  // Prioridade 3: Tem serviços, mas sem locais
  if (places === 0) {
    return {
      type: 'next-step',
      icon: '📍',
      message: 'Muito bem! Agora configure os locais de atendimento. Locais são os endereços físicos onde seus profissionais trabalham (clínica, consultório, salão). Você pode ter vários locais, e cada um pode ter diferentes profissionais e serviços disponíveis.',
      action: {
        label: 'Ir para Locais',
        link: '/places'
      }
    };
  }

  // Prioridade 4: Tem tudo, mas sem agendas
  if (schedules === 0) {
    return {
      type: 'next-step',
      icon: '📅',
      message: 'Quase lá! Agora crie uma agenda para que seus clientes possam agendar online. A agenda define os horários de disponibilidade de cada profissional em cada local. Depois de criar, você vai configurar os turnos (manhã, tarde, noite) e definir quais serviços estão disponíveis em cada período.',
      action: {
        label: 'Ir para Agendas',
        link: '/schedules'
      }
    };
  }

  // Tudo configurado!
  return {
    type: 'success',
    icon: '🎉',
    message: 'Parabéns! Seu sistema está totalmente configurado e pronto para uso! Agora seus clientes podem agendar horários diretamente pela página pública. Você pode gerenciar todos os agendamentos, acompanhar o fluxo de trabalho e ter uma visão completa da sua operação.',
    action: {
      label: 'Ver Agendamentos',
      link: '/appointments'
    }
  };
};

/**
 * Regras para a página de Profissionais
 */
const getProfessionalsHint = (data) => {
  const { items = [] } = data;

  // Se não tem profissionais
  if (items.length === 0) {
    return {
      type: 'action-required',
      icon: '👤',
      message: 'Esta é a base de tudo! Cadastre os profissionais que vão realizar os atendimentos. Preencha nome, especialidade, foto (opcional) e informações de contato. Depois você vai vincular eles aos locais e aos serviços que realizam.',
      action: {
        label: 'Criar Primeiro Profissional',
        link: '/professionals?create=1'
      }
    };
  }

  // Tem profissionais, próximo passo: serviços
  return {
    type: 'tip',
    icon: '✅',
    message: 'Profissionais cadastrados com sucesso! Agora é hora de definir quais serviços eles oferecem. Cada serviço terá sua duração, preço e poderá ser vinculado aos profissionais capacitados para realizá-lo.',
    action: {
      label: 'Ir para Serviços',
      link: '/services'
    }
  };
};

/**
 * Regras para a página de Serviços
 */
const getServicesHint = (data) => {
  const { items = [] } = data;

  // Se não tem serviços
  if (items.length === 0) {
    return {
      type: 'action-required',
      icon: '⚙️',
      message: 'Serviços são os procedimentos que você oferece aos seus clientes (ex: corte de cabelo, limpeza de pele, consulta). Defina o nome, duração estimada, preço base e categoria. Esses serviços serão oferecidos nos seus locais de atendimento.',
      action: {
        label: 'Criar Primeiro Serviço',
        link: '/services?create=1'
      }
    };
  }

  // Tem serviços, próximo passo: locais
  return {
    type: 'tip',
    icon: '✅',
    message: 'Serviços definidos! Próximo passo é configurar seus locais de atendimento. Cada local pode ter profissionais específicos e oferecer serviços diferentes, dando flexibilidade total para sua operação.',
    action: {
      label: 'Ir para Locais',
      link: '/places'
    }
  };
};

/**
 * Regras para a página de Locais
 */
const getPlacesHint = (data) => {
  const { places = [], data: normalizedData = {} } = data;
  const { professionals = [], services = [] } = normalizedData;

  // Se não tem locais
  if (places.length === 0) {
    return {
      type: 'action-required',
      icon: '🏢',
      message: 'Locais são os endereços físicos onde você atende (clínica, consultório, salão, etc). Cadastre cada local com nome, endereço completo e foto. Você pode ter múltiplos locais e gerenciar todos de forma centralizada.',
      action: {
        label: 'Criar Primeiro Local',
        link: '/places?wizard=1'
      }
    };
  }

  // Tem locais, verificar se tem profissionais vinculados
  const hasLinkedProfessionals = places.some(place => 
    place.professionals && place.professionals.length > 0
  );

  if (!hasLinkedProfessionals) {
    return {
      type: 'next-step',
      icon: '👥',
      message: 'Locais criados! Agora vincule os profissionais aos locais onde eles trabalham. Um profissional pode atender em vários locais diferentes, e cada local pode ter sua própria equipe. Abra um local e adicione os profissionais clicando em "Gerenciar Profissionais".'
    };
  }

  // Tem profissionais vinculados, verificar serviços
  const hasLinkedServices = places.some(place => 
    place.services && place.services.length > 0
  );

  if (!hasLinkedServices) {
    return {
      type: 'next-step',
      icon: '⚙️',
      message: 'Profissionais vinculados! Agora defina quais serviços são oferecidos em cada local. Nem todos os locais precisam oferecer todos os serviços - você tem flexibilidade total. Abra um local e clique em "Gerenciar Serviços" para configurar.'
    };
  }

  // Tudo configurado, sugerir formas de pagamento
  return {
    type: 'tip',
    icon: '💳',
    message: 'Locais totalmente configurados! Você pode refinar ainda mais clicando no ícone ⚙️ ao lado de cada serviço para definir formas de pagamento aceitas especificamente para aquele serviço naquele local.'
  };
};

/**
 * Regras para a página de Agendas
 */
const getSchedulesHint = (data) => {
  const { items = [], shifts = {} } = data;

  // Se não tem agendas
  if (items.length === 0) {
    return {
      type: 'action-required',
      icon: '📅',
      message: 'Agendas são a ponte entre você e seus clientes! Cada agenda representa a disponibilidade de um profissional em um local específico. Crie sua primeira agenda selecionando o profissional e o local, e depois você vai configurar os turnos (horários de trabalho).',
      action: {
        label: 'Criar Primeira Agenda',
        link: '/schedules?wizard=1'
      }
    };
  }

  // Tem agendas, verificar se tem turnos configurados
  const hasShiftsWithServices = items.some(schedule => {
    const scheduleShifts = shifts[schedule.id] || [];
    return scheduleShifts.some(shift => shift.serviceIds && shift.serviceIds.length > 0);
  });

  if (!hasShiftsWithServices) {
    return {
      type: 'next-step',
      icon: '⏰',
      message: 'Agenda criada! Agora configure os turnos de atendimento. Turnos definem os horários disponíveis (ex: Segunda a Sexta, 9h às 18h) e quais serviços podem ser agendados em cada período. Abra uma agenda e clique em "Gerenciar Turnos" para configurar. Você pode ter diferentes turnos para manhã, tarde e noite!'
    };
  }

  // Tudo configurado!
  return {
    type: 'success',
    icon: '🎊',
    message: 'Perfeito! Suas agendas estão prontas e funcionando! Agora você pode compartilhar o link público com seus clientes para que eles mesmos façam os agendamentos online, 24 horas por dia. Clique no ícone de compartilhar 🔗 em cada agenda para pegar o link. Seus clientes vão adorar a praticidade!',
    action: {
      label: 'Ver Agendamentos',
      link: '/appointments'
    }
  };
};

/**
 * Hint padrão quando não há nenhuma dica específica
 * Exibido quando o sistema está totalmente configurado
 */
const getDefaultHint = () => {
  return {
    type: 'tip',
    icon: '🚀',
    message: 'Sistema funcionando perfeitamente! Não esqueça de compartilhar suas agendas com seus clientes para que eles possam agendar de forma autônoma. Clique no ícone 🔗 em qualquer agenda para copiar o link público. Seus clientes acessam, escolhem o horário e pronto - você recebe a notificação! É rápido, fácil e seus clientes adoram a autonomia.',
    action: {
      label: 'Ver Agendas',
      link: '/schedules'
    }
  };
};

/**
 * Tipos de dica disponíveis
 */
export const HINT_TYPES = {
  WELCOME: 'welcome',           // Boas-vindas
  ACTION_REQUIRED: 'action-required', // Ação necessária
  NEXT_STEP: 'next-step',       // Próximo passo sugerido
  TIP: 'tip',                   // Dica útil
  SUCCESS: 'success',           // Tudo certo
};

/**
 * Mapeamento de tipos para severidade do Alert do MUI
 */
export const HINT_SEVERITY_MAP = {
  [HINT_TYPES.WELCOME]: 'info',
  [HINT_TYPES.ACTION_REQUIRED]: 'warning',
  [HINT_TYPES.NEXT_STEP]: 'info',
  [HINT_TYPES.TIP]: 'info',
  [HINT_TYPES.SUCCESS]: 'success',
};
